package com.lomdaat.ads.internal

import co.touchlab.kermit.Logger
import com.lomdaat.ads.api.AdsClient
import com.lomdaat.ads.internal.dto.AudioAdDto
import com.lomdaat.ads.internal.dto.NativeAdDto
import com.lomdaat.ads.internal.mapper.toModel
import com.lomdaat.ads.model.AudioAd
import com.lomdaat.ads.model.AudioAdRequest
import com.lomdaat.ads.model.BannerAdRequest
import com.lomdaat.ads.model.InterstitialAdRequest
import com.lomdaat.ads.model.NativeAd
import com.lomdaat.ads.model.NativeAdRequest
import com.lomdaat.ads.model.VideoAd
import com.lomdaat.ads.model.VideoAdTagRequest
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.url
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.HttpStatusCode
import io.ktor.http.URLBuilder
import io.ktor.http.parameters
import kotlinx.serialization.json.Json

internal class DefaultAdsClient(
    private val baseUrl: String,
    json: Json,
    private val logger: Logger = Logger.withTag("AdsClient"),
    private val httpClient: HttpClient = createAdsHttpClient(json),
) : AdsClient {

    override suspend fun fetchAudioAd(request: AudioAdRequest): AudioAd? {
        return getNullable("/a", request.requestId?.value, request.appId.value, request.location, AudioAdDto::class)
            ?.toModel()
    }

    override suspend fun fetchNativeAd(request: NativeAdRequest): NativeAd? {
        return getNullable("/n", request.requestId?.value, request.appId.value, request.location, NativeAdDto::class)
            ?.toModel()
    }

    override fun buildBannerUrl(request: BannerAdRequest): String = buildUrl("/b") {
        appendCommonParams(request.requestId?.value, request.appId.value, request.location)
        parameters.append("size", request.size.wireValue)
        parameters.append("reload_auto", request.reloadAutomatically.toString())
    }

    override fun buildInterstitialUrl(request: InterstitialAdRequest): String = buildUrl("/i") {
        appendCommonParams(request.requestId?.value, request.appId.value, request.location)
        parameters.append("hadran", request.hadran.toString())
    }

    override suspend fun fetchVerticalVideoAd(request: VideoAdTagRequest): VideoAd? {
        val url = buildUrl("/vv/vast") {
            appendCommonParams(request.requestId?.value, request.appId.value, request.location)
            request.clickUrlPrefix?.let { parameters.append("click_url_prefix", it) }
        }
        val response = runCatching { httpClient.get { url(url) } }
            .onFailure { logger.w(it) { "Failed to fetch vertical video ad" } }
            .getOrNull() ?: return null
        if (response.status == HttpStatusCode.NoContent) return null
        if (response.status.value !in 200..299) {
            logger.w { "Unexpected /vv/vast status ${response.status}" }
            return null
        }
        val body = runCatching { response.bodyAsText() }.getOrNull() ?: return null
        return VastParser.parse(body)
    }

    override fun buildVideoVastUrl(request: VideoAdTagRequest): String = buildUrl("/v/vast") {
        appendCommonParams(request.requestId?.value, request.appId.value, request.location)
        request.clickUrlPrefix?.let { parameters.append("click_url_prefix", it) }
    }

    override fun buildVideoVmapUrl(request: VideoAdTagRequest): String = buildUrl("/v/vmap") {
        appendCommonParams(request.requestId?.value, request.appId.value, request.location)
        request.durationMs?.let { parameters.append("duration", it.toString()) }
        request.clickUrlPrefix?.let { parameters.append("click_url_prefix", it) }
    }

    override suspend fun trackEvent(url: String): Boolean {
        return runCatching {
            val response = httpClient.post { url(url) }
            response.status.value in 200..299
        }.onFailure {
            logger.w(it) { "Failed to track event at $url" }
        }.getOrDefault(false)
    }

    private suspend fun <T : Any> getNullable(
        path: String,
        requestId: String?,
        appId: String,
        location: com.lomdaat.ads.model.GeoPoint?,
        type: kotlin.reflect.KClass<T>,
    ): T? {
        val response = runCatching {
            httpClient.get {
                url(buildUrl(path) {
                    appendCommonParams(requestId, appId, location)
                })
            }
        }.onFailure {
            logger.w(it) { "Failed to fetch ad from $path" }
        }.getOrNull() ?: return null

        return response.decodeNullable(type)
    }

    private suspend fun <T : Any> HttpResponse.decodeNullable(type: kotlin.reflect.KClass<T>): T? {
        if (status == HttpStatusCode.NoContent) return null
        if (status.value !in 200..299) {
            logger.w { "Unexpected ads response status $status: ${bodyAsText()}" }
            return null
        }
        @Suppress("UNCHECKED_CAST")
        return when (type) {
            AudioAdDto::class -> body<AudioAdDto>() as T
            NativeAdDto::class -> body<NativeAdDto>() as T
            else -> error("Unsupported decode type: $type")
        }
    }

    private fun buildUrl(path: String, block: URLBuilder.() -> Unit = {}): String {
        return URLBuilder(
            "$baseUrl${if (path.startsWith("/")) path else "/$path"}"
        ).apply {
            block()
        }.buildString()
    }

    private fun URLBuilder.appendCommonParams(
        requestId: String?,
        appId: String,
        location: com.lomdaat.ads.model.GeoPoint?,
    ) {
        requestId?.let { parameters.append("uid", it) }
        parameters.append("app", appId)
        location?.let {
            parameters.append("lat", it.latitude.toString())
            parameters.append("lon", it.longitude.toString())
        }
    }
}
